<script setup lang="ts">
  import { useFocus } from '@vueuse/core';
  import currencyLib from 'currency-codes';
  import type { CurrencyCodeRecord } from 'currency-codes';
  import { computed, nextTick, ref, useTemplateRef } from 'vue';
  import type { ComponentPublicInstance, HTMLAttributes } from 'vue';

  import { ComboSelect } from '@ui/components/combo-select';
  import { NumberField, NumberFieldContent, NumberFieldInput } from '@ui/components/number-field';
  import type { NumberFieldInputProps } from '@ui/components/number-field/NumberFieldInput.vue';
  import { cn } from '@ui/utils/utils';

  const props = withDefaults(
    defineProps<
      {
        placeholder?: string;
        hideCurrencySelect?: boolean;
        smallestUnit?: boolean;
        min?: number;
        max?: number;
        hideDecimals?: boolean; // for display purposes
        inputClass?: HTMLAttributes['class'];
        selection?: 'all' | 'start' | 'decimal';
        disabled?: boolean;
        monotype?: boolean;
      } & Omit<NumberFieldInputProps, 'class'>
    >(),
    {
      monotype: true,
      size: 'sm',
      align: 'right',
    },
  );

  const emit = defineEmits<{
    (e: 'handleEnter', value): void;
    (e: 'handleEscape', value): void;
  }>();

  const currencyCode = defineModel<string | null | undefined>('currency');
  const value = defineModel<number>();
  const focused = ref(false);
  const numberInput = useTemplateRef<ComponentPublicInstance>('numberField');
  const { focused: numberInputFocus } = useFocus(numberInput);

  const currencyRecord = computed<CurrencyCodeRecord>(
    () => currencyLib.code(currencyCode.value || 'USD') as CurrencyCodeRecord,
  );

  const decimalFactor = computed<number>(() =>
    props.smallestUnit ? 10 ** currencyRecord.value.digits : 1,
  );

  const valueCopy = computed<number | null | undefined>(() => {
    if (value.value == null) return null;
    return value.value / decimalFactor.value;
  });

  const focusedFormatOptions = computed(() => ({
    maximumFractionDigits: props.hideDecimals ? 0 : currencyRecord.value.digits,
    minimumFractionDigits: props.hideDecimals ? 0 : currencyRecord.value.digits,
    useGrouping: false,
  }));

  const formatOptions = computed(() => {
    const fractionDigits = props.hideDecimals ? 0 : currencyRecord.value.digits;

    if (focused.value) {
      return focusedFormatOptions.value;
    }

    return props.hideCurrencySelect
      ? {
          currency: currencyRecord.value.code,
          currencyDisplay: 'symbol' as const,
          currencySign: 'accounting' as const,
          maximumFractionDigits: fractionDigits,
          minimumFractionDigits: fractionDigits,
          style: 'currency' as const,
        }
      : {
          maximumFractionDigits: fractionDigits,
          minimumFractionDigits: fractionDigits,
        };
  });

  const setFocusSelection = () => {
    const element = numberInput.value?.$el;

    if (element) {
      const index = element.value.indexOf('.');

      switch (props.selection) {
        case 'start':
          return element.setSelectionRange(1, 1);
        case 'decimal':
          if (index !== -1) {
            return element.setSelectionRange(index, index);
          }

          return element.select();
        case 'all':
          return element.select();
        default:
      }
    }
  };

  function handlePaste(e: ClipboardEvent) {
    const raw = e.clipboardData?.getData('text') ?? '';
    const cleaned = raw.replace(/[^0-9.\-]/g, '');
    if (cleaned === raw.trim()) return;
    e.preventDefault();
    const el = e.target as HTMLInputElement;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    el.setRangeText(cleaned, start, end, 'end');
    el.dispatchEvent(
      new InputEvent('input', { bubbles: true, inputType: 'insertText', data: cleaned }),
    );
  }

  defineExpose({
    focusNumberInput: () => {
      numberInputFocus.value = true;
      setFocusSelection();
    },
    numberInput,
  });
</script>

<template>
  <div
    :data-state="focused ? 'focused' : ''"
    :class="
      cn('grid grid-cols-[min-content_1fr] border h-fit rounded-sm', focused && 'border-foreground')
    "
  >
    <ComboSelect
      v-if="!hideCurrencySelect"
      :options="currencyLib.data"
      searchable
      :disabled="disabled"
      :model-value="currencyRecord"
      class="rounded-none rounded-l"
      btn-class="rounded-none rounded-l rounded-r-none border-0 border-r w-18 text-2xs"
      variant="dropdown"
      placeholder="Set Currency"
      search-placeholder="Set Currency..."
      :search-attrs="['currency']"
      option-identifier="code"
      @update:model-value="
        (v) => (currencyCode = v ? (Array.isArray(v) ? v[0].code : v.code) : null)
      "
    >
      <template #option="{ option }">
        <span class="line-clamp-1"> {{ option.code }} – {{ option.currency }} </span>
      </template>
    </ComboSelect>

    <NumberField
      :model-value="valueCopy"
      :default-value="0"
      :step="hideDecimals ? 1 : 0.01"
      :min="min && min / decimalFactor"
      :max="max && max / decimalFactor"
      class="w-full col-start-2"
      :class="{ 'font-mono': monotype }"
      :format-options="formatOptions"
      :disabled="disabled"
      @update:model-value="
        (v) => {
          const roundingFactor = 10 ** currencyRecord.digits;
          const rounded = Math.round((v || 0) * roundingFactor) / roundingFactor;
          value = smallestUnit ? Math.round(rounded * decimalFactor) : rounded;
        }
      "
    >
      <NumberFieldContent>
        <NumberFieldInput
          ref="numberField"
          class="border-none border-l mn-input"
          :class="inputClass"
          :align
          :size
          :placeholder="placeholder"
          @focus="
            () => {
              focused = true;
              // Synchronously update the DOM value so the parser and input are in sync.
              // formatOptions changes immediately (reactiveComputed), but Vue's watcher
              // that updates inputValue is async — if the user types before it fires,
              // onBeforeinput builds nextValue from the old currency-formatted string
              // (e.g. '$100.00') which the new decimal-style parser rejects.
              const el = numberInput?.$el;
              if (el) {
                el.value =
                  valueCopy != null
                    ? new Intl.NumberFormat(undefined, focusedFormatOptions).format(valueCopy)
                    : '';
              }
              nextTick(setFocusSelection);
            }
          "
          @blur="() => (focused = false)"
          @mousedown.middle="$event.preventDefault()"
          @auxclick.stop="$event.preventDefault()"
          @keyup.enter="
            (e) => {
              e.target.blur();
              emit('handleEnter', value);
            }
          "
          @keyup.escape="
            (e) => {
              e.target.blur();
              emit('handleEscape', value);
            }
          "
          @wheel="
            (e) => {
              e.preventDefault();
              e.target.blur();
            }
          "
          @paste="handlePaste"
        />
      </NumberFieldContent>
    </NumberField>
  </div>
</template>

<style scoped></style>
